import type { Modal } from '@ps/web-core-store';
import React, { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import type { RouteComponentProps } from 'react-router';
import { MemoryRouter, Route, useHistory, withRouter } from 'react-router';

import { CMSReferences } from '@swf/cms';
import { CacheControl } from '@swf/components/ResponseHeaders/ResponseHeaders';
import StarsModal from '@swf/components/StarsModal/StarsModal';
import useCMSHeaderAndGridLayout from '@swf/core/hooks/useCMSHeaderAndGridLayout';
import useInactivityTimeout from '@swf/core/hooks/useInactivityTimeout';
import { select } from '@swf/core/store';
import { FirstAuthLoadingPage, FirstLoader, InactivityTimeoutPrompt } from '@swf/first';

import { TXTWCL_WeXre_working_on_this } from './FirstRoute.messages.json';

const FirstRoute: React.FC<
  RouteComponentProps & {
    component: React.ComponentType;
    noCache?: boolean;
    requireAuth?: boolean;
  }
> = ({ component, noCache, requireAuth = true }) => {
  const history = useHistory();
  const { path, locationState } = (useSelector(select.modal.getCurrent) || {}) as Partial<Modal>;
  const redirectTo = useSelector(select.cms.getRedirectUrl);
  const isAuthenticated = useSelector(select.session.isAuthenticated);
  const hasClientSession = useSelector(select.session.hasClientSession);
  const userWebId = useSelector(select.session.getWebId);
  const userWebIdRef = useRef(userWebId);
  const isEmbedded = useSelector(select.device.isEmbedded);
  const website = useSelector(select.cms.getWebsite);
  const cmsHeaderAndGridLayout = useCMSHeaderAndGridLayout();
  const { showInactivityPrompt, remainingSeconds, setShowInactivityPrompt } =
    useInactivityTimeout();

  useEffect(() => {
    if (hasClientSession && isAuthenticated) {
      userWebIdRef.current = userWebId;
    }
    if (userWebIdRef.current && userWebIdRef.current !== userWebId && redirectTo) {
      history.push(redirectTo); // go lobby when logout
    }
  }, [isAuthenticated, hasClientSession, history, userWebId, redirectTo, userWebIdRef]);

  return (
    <>
      {noCache && <CacheControl>no-cache</CacheControl>}
      {!isEmbedded && cmsHeaderAndGridLayout && <CMSReferences references={website?.site_header} />}
      <MemoryRouter
        keyLength={process.env.NODE_ENV === 'test' ? 0 : 6}
        initialEntries={[{ pathname: path, state: locationState }]}
      >
        {showInactivityPrompt && (
          <Route
            render={() => (
              <InactivityTimeoutPrompt
                remainingSeconds={remainingSeconds}
                onClose={() => setShowInactivityPrompt(false)}
              />
            )}
          />
        )}
        {!hasClientSession ? (
          <FirstLoader label={TXTWCL_WeXre_working_on_this} />
        ) : !requireAuth ? (
          React.createElement(component)
        ) : (
          !(userWebIdRef.current && userWebIdRef.current !== userWebId) &&
          (isAuthenticated ? React.createElement(component) : <FirstAuthLoadingPage />)
        )}
      </MemoryRouter>
      {!isEmbedded && cmsHeaderAndGridLayout && (
        <>
          <CMSReferences references={website?.site_footer} />
          <StarsModal />
        </>
      )}
    </>
  );
};

export default withRouter(FirstRoute);
